import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { BaseTexture } from "@babylonjs/core/Materials/Textures/baseTexture";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { Scene } from "@babylonjs/core/scene";
import { GROUND } from "./palette";

/** Small seeded PRNG so procedural textures are identical on every load (stable screenshots). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type Ctx2D = CanvasRenderingContext2D;

export function makeDynamicTexture(scene: Scene, name: string, width: number, height: number, opts: { alpha?: boolean; mips?: boolean; wrap?: boolean } = {}): DynamicTexture {
  const tex = new DynamicTexture(name, { width, height }, scene, opts.mips ?? true, Texture.TRILINEAR_SAMPLINGMODE);
  if (opts.alpha) tex.hasAlpha = true;
  if (opts.wrap) {
    tex.wrapU = Texture.WRAP_ADDRESSMODE;
    tex.wrapV = Texture.WRAP_ADDRESSMODE;
  }
  tex.anisotropicFilteringLevel = 8;
  return tex;
}

/** The DynamicTexture context is a real 2D canvas context in the browser. */
export function ctx2d(tex: DynamicTexture): Ctx2D {
  return tex.getContext() as unknown as Ctx2D;
}

function speckle(ctx: Ctx2D, size: number, rnd: () => number, colors: string[], count: number, maxDot: number): void {
  for (let i = 0; i < count; i++) {
    ctx.fillStyle = colors[Math.floor(rnd() * colors.length)]!;
    const w = 1 + Math.floor(rnd() * maxDot);
    const h = 1 + Math.floor(rnd() * maxDot);
    ctx.fillRect(Math.floor(rnd() * size), Math.floor(rnd() * size), w, h);
  }
}

function paintNoise(tex: DynamicTexture, base: string, specks: string[], seed: number, count: number, maxDot: number): void {
  const size = tex.getSize().width;
  const ctx = ctx2d(tex);
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);
  speckle(ctx, size, mulberry32(seed), specks, count, maxDot);
  tex.update();
}

function paintGrid(tex: DynamicTexture, base: string, alt: string | null, line: string, cells: number, seed: number, checker: boolean): void {
  const size = tex.getSize().width;
  const ctx = ctx2d(tex);
  const cell = size / cells;
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);
  if (alt) {
    ctx.fillStyle = alt;
    for (let i = 0; i < cells; i++) {
      for (let j = 0; j < cells; j++) {
        if (checker ? (i + j) % 2 === 1 : false) ctx.fillRect(i * cell, j * cell, cell, cell);
      }
    }
  }
  speckle(ctx, size, mulberry32(seed), [alt ?? line, line], 260, 3);
  ctx.fillStyle = line;
  for (let i = 0; i <= cells; i++) {
    ctx.fillRect(Math.round(i * cell) - 1, 0, 2, size);
    ctx.fillRect(0, Math.round(i * cell) - 1, size, 2);
  }
  tex.update();
}

function paintPlanks(tex: DynamicTexture, base: string, alt: string, seed: number): void {
  const size = tex.getSize().width;
  const ctx = ctx2d(tex);
  const rnd = mulberry32(seed);
  const rows = 8;
  const rowH = size / rows;
  for (let r = 0; r < rows; r++) {
    ctx.fillStyle = r % 2 === 0 ? base : alt;
    ctx.fillRect(0, r * rowH, size, rowH);
    // plank ends
    ctx.fillStyle = "rgba(0,0,0,0.18)";
    const offset = Math.floor(rnd() * size);
    ctx.fillRect(offset, r * rowH, 2, rowH);
    ctx.fillRect((offset + size / 2) % size, r * rowH, 2, rowH);
    ctx.fillRect(0, r * rowH, size, 2);
  }
  speckle(ctx, size, rnd, ["rgba(0,0,0,0.08)", "rgba(255,255,255,0.08)"], 300, 6);
  tex.update();
}

/** Soft carpet: fine two-tone noise with a faint weave grid. */
function paintCarpet(tex: DynamicTexture, base: string, alt: string, seed: number): void {
  const size = tex.getSize().width;
  const ctx = ctx2d(tex);
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);
  speckle(ctx, size, mulberry32(seed), [alt, "rgba(255,255,255,0.10)", "rgba(0,0,0,0.06)"], 5200, 2);
  ctx.fillStyle = "rgba(0,0,0,0.035)";
  for (let i = 0; i < size; i += 8) {
    ctx.fillRect(i, 0, 1, size);
    ctx.fillRect(0, i, size, 1);
  }
  tex.update();
}

/** Irregular flagstones with mortar lines. */
function paintStone(tex: DynamicTexture, seed: number): void {
  const size = tex.getSize().width;
  const ctx = ctx2d(tex);
  const rnd = mulberry32(seed);
  ctx.fillStyle = GROUND.stoneMortar;
  ctx.fillRect(0, 0, size, size);
  const rows = 4;
  const rowH = size / rows;
  const tones = [GROUND.stoneFloor, GROUND.stoneFloorAlt, "#B7AFA2", "#C9C2B6"];
  for (let r = 0; r < rows; r++) {
    let x = r % 2 === 0 ? 0 : -rowH * 0.5;
    while (x < size) {
      const w = rowH * (0.8 + rnd() * 0.7);
      ctx.fillStyle = tones[Math.floor(rnd() * tones.length)]!;
      ctx.fillRect(Math.round(x) + 3, Math.round(r * rowH) + 3, Math.round(w) - 6, Math.round(rowH) - 6);
      // wrap the last stone so the texture tiles
      if (x + w > size) ctx.fillRect(Math.round(x - size) + 3, Math.round(r * rowH) + 3, Math.round(w) - 6, Math.round(rowH) - 6);
      x += w;
    }
  }
  speckle(ctx, size, rnd, ["rgba(0,0,0,0.07)", "rgba(255,255,255,0.08)"], 900, 3);
  tex.update();
}

/** Terrazzo: a pale base with scattered coloured chips. */
function paintTerrazzo(tex: DynamicTexture, base: string, chips: string[], seed: number): void {
  const size = tex.getSize().width;
  const ctx = ctx2d(tex);
  const rnd = mulberry32(seed);
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 520; i++) {
    ctx.fillStyle = chips[Math.floor(rnd() * chips.length)]!;
    const x = rnd() * size;
    const y = rnd() * size;
    const r = 1.5 + rnd() * 4.5;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    for (let k = 1; k < 5; k++) {
      const a = (k / 5) * Math.PI * 2 + rnd() * 0.6;
      ctx.lineTo(x + Math.cos(a) * r * (0.6 + rnd() * 0.5), y + Math.sin(a) * r * (0.6 + rnd() * 0.5));
    }
    ctx.closePath();
    ctx.fill();
  }
  tex.update();
}

/** Polished concrete: soft mottling and saw-cut joints at the tile edges. */
function paintConcrete(tex: DynamicTexture, seed: number): void {
  const size = tex.getSize().width;
  const ctx = ctx2d(tex);
  const rnd = mulberry32(seed);
  ctx.fillStyle = "#B9B7B1";
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 60; i++) {
    ctx.fillStyle = rnd() > 0.5 ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)";
    const r = 10 + rnd() * 40;
    ctx.beginPath();
    ctx.arc(rnd() * size, rnd() * size, r, 0, Math.PI * 2);
    ctx.fill();
  }
  speckle(ctx, size, rnd, ["rgba(0,0,0,0.12)", "rgba(255,255,255,0.12)"], 900, 2);
  ctx.fillStyle = "rgba(0,0,0,0.22)";
  ctx.fillRect(0, 0, size, 2);
  ctx.fillRect(0, 0, 2, size);
  tex.update();
}

/** Rubber gym flooring: near-black with coloured flecks and faint tile seams. */
function paintRubber(tex: DynamicTexture, seed: number): void {
  const size = tex.getSize().width;
  const ctx = ctx2d(tex);
  ctx.fillStyle = "#34373D";
  ctx.fillRect(0, 0, size, size);
  speckle(ctx, size, mulberry32(seed), ["#4A4E56", "#2A2C31", "#5C7FBF", "#D9473B", "#8E96A3"], 2600, 2);
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.fillRect(0, 0, size, 2);
  ctx.fillRect(0, 0, 2, size);
  tex.update();
}

/** Painted plaster (tinted by the wall colour): almost white with a faint roller texture. */
function paintPlaster(tex: DynamicTexture, seed: number): void {
  const size = tex.getSize().width;
  const ctx = ctx2d(tex);
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, size, size);
  speckle(ctx, size, mulberry32(seed), ["rgba(0,0,0,0.035)", "rgba(0,0,0,0.02)", "rgba(255,255,255,0.5)"], 1800, 3);
  tex.update();
}

/** Running-bond brick (one texture repeat = 1.2 m: 5 bricks x 16 courses). */
function paintBrick(tex: DynamicTexture, seed: number): void {
  const size = tex.getSize().width;
  const ctx = ctx2d(tex);
  const rnd = mulberry32(seed);
  ctx.fillStyle = "#D9CFC2";
  ctx.fillRect(0, 0, size, size);
  const rows = 16;
  const cols = 5;
  const rh = size / rows;
  const cw = size / cols;
  const tones = ["#A8553D", "#B5634A", "#9C4C36", "#B86E52", "#A05A44", "#8F4A35"];
  for (let r = 0; r < rows; r++) {
    const off = r % 2 === 0 ? 0 : cw / 2;
    for (let c = -1; c < cols + 1; c++) {
      ctx.fillStyle = tones[Math.floor(rnd() * tones.length)]!;
      const x = Math.round(c * cw + off);
      ctx.fillRect(x + 1, Math.round(r * rh) + 1, Math.round(cw) - 2, Math.round(rh) - 2);
    }
  }
  speckle(ctx, size, rnd, ["rgba(0,0,0,0.08)", "rgba(255,255,255,0.06)"], 1400, 2);
  tex.update();
}

export type FloorKind = "wood" | "tile" | "carpet" | "stone";
/** Store interior floors (packages/world StoreFloor). */
export type StoreFloorKind = "wood_light" | "wood_dark" | "tile_white" | "checker" | "concrete" | "rubber" | "carpet" | "terrazzo";

export class MaterialKit {
  /** Vertex-coloured material for every merged static layer. */
  readonly town: StandardMaterial;
  readonly grass: StandardMaterial;
  readonly asphalt: StandardMaterial;
  readonly paver: StandardMaterial;
  readonly plaza: StandardMaterial;
  readonly wood: StandardMaterial;
  readonly tile: StandardMaterial;
  readonly carpet: StandardMaterial;
  readonly stone: StandardMaterial;
  readonly glow: StandardMaterial;
  /** Pool / hot tub / aquarium water (vertex coloured, glossy, faintly self-lit). */
  readonly water: StandardMaterial;
  /** Uncommitted placement ghost: cobalt, translucent (docs/01 section 6 "visibly uncommitted"). */
  readonly ghost: StandardMaterial;
  /** Free-anchor markers and the ghost's footprint outline. */
  readonly marker: StandardMaterial;
  private readonly textures: DynamicTexture[] = [];
  private readonly signMaterials = new Map<BaseTexture, StandardMaterial>();
  private readonly colorSigns = new Map<string, StandardMaterial>();
  private readonly storeFloors = new Map<StoreFloorKind, StandardMaterial>();
  private readonly lazy: StandardMaterial[] = [];
  private plasterTex: DynamicTexture | null = null;
  private brickTex: DynamicTexture | null = null;
  private glassMat: StandardMaterial | null = null;
  private unlitMat: StandardMaterial | null = null;

  constructor(readonly scene: Scene) {
    this.town = new StandardMaterial("townVertexColor", scene);
    this.town.diffuseColor = Color3.White();
    this.town.specularColor = new Color3(0.05, 0.05, 0.05);
    this.town.specularPower = 24;
    this.town.ambientColor = Color3.White();

    this.grass = this.ground("grass", 6, (t) => paintNoise(t, GROUND.grass, [GROUND.grassAlt, "#97CC78", "#79AE5A"], 11, 2600, 3));
    this.asphalt = this.ground("asphalt", 5, (t) => paintNoise(t, GROUND.asphalt, [GROUND.asphaltAlt, "#666A73", "#4C5058"], 23, 3200, 2));
    this.paver = this.ground("paver", 4, (t) => paintGrid(t, GROUND.paver, null, GROUND.paverLine, 4, 31, false));
    this.plaza = this.ground("plaza", 6, (t) => paintGrid(t, GROUND.plaza, GROUND.plazaAlt, GROUND.plazaLine, 4, 47, true));
    this.wood = this.ground("wood", 4, (t) => paintPlanks(t, GROUND.woodFloor, GROUND.woodFloorAlt, 53));
    this.tile = this.ground("tile", 4, (t) => paintGrid(t, GROUND.tileFloor, GROUND.tileFloorAlt, "#C9C2B4", 4, 61, true));
    this.carpet = this.ground("carpet", 3, (t) => paintCarpet(t, GROUND.carpet, GROUND.carpetAlt, 67));
    this.stone = this.ground("stone", 3.2, (t) => paintStone(t, 71));

    this.glow = new StandardMaterial("selectionGlow", scene);
    this.glow.diffuseColor = Color3.FromHexString("#2851D9");
    this.glow.emissiveColor = Color3.FromHexString("#2851D9").scale(0.8);
    this.glow.specularColor = Color3.Black();
    this.glow.alpha = 0.16;
    this.glow.backFaceCulling = false;

    this.water = new StandardMaterial("water", scene);
    this.water.diffuseColor = Color3.White();
    this.water.specularColor = new Color3(0.85, 0.9, 0.95);
    this.water.specularPower = 90;
    this.water.emissiveColor = new Color3(0.06, 0.14, 0.2);
    this.water.ambientColor = Color3.White();

    this.ghost = new StandardMaterial("placementGhost", scene);
    this.ghost.diffuseColor = Color3.FromHexString("#2851D9");
    this.ghost.emissiveColor = Color3.FromHexString("#2851D9").scale(0.55);
    this.ghost.specularColor = Color3.Black();
    this.ghost.alpha = 0.5;
    // Depth pre-pass: only the ghost's front-most surfaces are blended (a clean translucent silhouette).
    this.ghost.needDepthPrePass = true;

    this.marker = new StandardMaterial("anchorMarker", scene);
    this.marker.diffuseColor = Color3.FromHexString("#2851D9");
    this.marker.emissiveColor = Color3.FromHexString("#5B7FEA").scale(0.7);
    this.marker.specularColor = Color3.Black();
    this.marker.disableLighting = false;
    this.marker.alpha = 0.6;
    this.marker.backFaceCulling = false;
    this.marker.zOffset = -2;
  }

  floor(kind: FloorKind): StandardMaterial {
    switch (kind) {
      case "tile":
        return this.tile;
      case "carpet":
        return this.carpet;
      case "stone":
        return this.stone;
      case "wood":
      default:
        return this.wood;
    }
  }

  /** Store floor material (created on first use, then shared by every store with that floor). */
  storeFloor(kind: StoreFloorKind): StandardMaterial {
    const existing = this.storeFloors.get(kind);
    if (existing) return existing;
    let mat: StandardMaterial;
    switch (kind) {
      case "wood_light":
        mat = this.ground("sfWoodLight", 4, (t) => paintPlanks(t, "#DDBF92", "#D1B17F", 101));
        break;
      case "wood_dark":
        mat = this.ground("sfWoodDark", 4, (t) => paintPlanks(t, "#8A5E3B", "#7A5133", 103));
        break;
      case "tile_white":
        mat = this.ground("sfTileWhite", 2.4, (t) => paintGrid(t, "#F4F2EC", null, "#D5D1C7", 4, 107, false));
        break;
      case "checker":
        mat = this.ground("sfChecker", 2.4, (t) => paintGrid(t, "#F3F0E8", "#4A5262", "#C9C5BC", 4, 109, true));
        break;
      case "concrete":
        mat = this.ground("sfConcrete", 4, (t) => paintConcrete(t, 113));
        break;
      case "rubber":
        mat = this.ground("sfRubber", 2, (t) => paintRubber(t, 127));
        break;
      case "terrazzo":
        mat = this.ground("sfTerrazzo", 2.5, (t) => paintTerrazzo(t, "#EFE9DF", ["#C9B9A6", "#9AA3AE", "#D98C7A", "#7FA88C", "#F2D3A2", "#5E6672"], 131));
        break;
      case "carpet":
      default:
        mat = this.carpet;
        break;
    }
    if (mat !== this.carpet) this.lazy.push(mat);
    this.storeFloors.set(kind, mat);
    return mat;
  }

  /** Wall textures for store rooms (tinted plaster or brick), world-space UVs set by the room builder. */
  wallTexture(kind: "plaster" | "brick"): DynamicTexture {
    if (kind === "brick") {
      if (!this.brickTex) {
        this.brickTex = makeDynamicTexture(this.scene, "brickTex", 256, 256, { wrap: true });
        paintBrick(this.brickTex, 137);
      }
      return this.brickTex;
    }
    if (!this.plasterTex) {
      this.plasterTex = makeDynamicTexture(this.scene, "plasterTex", 256, 256, { wrap: true });
      paintPlaster(this.plasterTex, 139);
    }
    return this.plasterTex;
  }

  /** Display-case / fridge / aquarium glass: vertex coloured, translucent, glossy. */
  get fixtureGlass(): StandardMaterial {
    if (!this.glassMat) {
      const m = new StandardMaterial("fixtureGlass", this.scene);
      m.diffuseColor = Color3.White();
      m.specularColor = new Color3(0.9, 0.95, 1);
      m.specularPower = 96;
      m.alpha = 0.28;
      m.ambientColor = Color3.White();
      this.glassMat = m;
    }
    return this.glassMat;
  }

  /** Unlit vertex colours: screens, lit tanks, oven windows, lamp shades, brake lights. */
  get unlitVertex(): StandardMaterial {
    if (!this.unlitMat) {
      const m = new StandardMaterial("unlitVertex", this.scene);
      // With lighting disabled only the emissive term lights the surface: white emissive x vertex colour.
      m.disableLighting = true;
      m.diffuseColor = Color3.Black();
      m.emissiveColor = Color3.White();
      m.ambientColor = Color3.Black();
      m.specularColor = Color3.Black();
      this.unlitMat = m;
    }
    return this.unlitMat;
  }

  /** World-space tiled ground material: one texture repeat every `metresPerTile` metres via UVs. */
  private ground(name: string, metresPerTile: number, paint: (t: DynamicTexture) => void): StandardMaterial {
    const tex = makeDynamicTexture(this.scene, `${name}Tex`, 256, 256, { wrap: true });
    paint(tex);
    this.textures.push(tex);
    const mat = new StandardMaterial(`${name}Mat`, this.scene);
    mat.diffuseTexture = tex;
    mat.specularColor = new Color3(0.03, 0.03, 0.03);
    mat.metadata = { metresPerTile };
    return mat;
  }

  metresPerTile(mat: StandardMaterial): number {
    const v = (mat.metadata as { metresPerTile?: number } | null)?.metresPerTile;
    return typeof v === "number" ? v : 4;
  }

  /** Unlit textured material (signs, tags, mats). One material per texture, reused. */
  signMaterial(tex: BaseTexture, opts: { alpha?: boolean } = {}): StandardMaterial {
    const existing = this.signMaterials.get(tex);
    if (existing) return existing;
    // Babylon's standard shader ADDS the emissive texture to emissiveColor, and adds the scene
    // ambient term; both must be black so the texture reaches the screen unchanged.
    const mat = new StandardMaterial(`sign_${tex.name}`, this.scene);
    mat.disableLighting = true;
    mat.emissiveColor = Color3.Black();
    mat.emissiveTexture = tex;
    mat.diffuseColor = Color3.Black();
    mat.ambientColor = Color3.Black();
    mat.specularColor = Color3.Black();
    if (opts.alpha) {
      mat.opacityTexture = tex;
      mat.backFaceCulling = false;
    }
    this.signMaterials.set(tex, mat);
    return mat;
  }

  /**
   * Unlit flat-colour sign board, one per colour (round 4: the far-away placeholder of a branded
   * sign whose texture is created lazily when the camera comes near).
   */
  colorSignMaterial(hex: string): StandardMaterial {
    const key = hex.toUpperCase();
    const existing = this.colorSigns.get(key);
    if (existing) return existing;
    const mat = new StandardMaterial(`signColor_${key}`, this.scene);
    mat.disableLighting = true;
    mat.emissiveColor = Color3.FromHexString(key);
    mat.diffuseColor = Color3.Black();
    mat.ambientColor = Color3.Black();
    mat.specularColor = Color3.Black();
    this.colorSigns.set(key, mat);
    return mat;
  }

  releaseSignMaterial(tex: BaseTexture): void {
    const mat = this.signMaterials.get(tex);
    if (!mat) return;
    this.signMaterials.delete(tex);
    mat.dispose();
  }

  dispose(): void {
    for (const m of this.signMaterials.values()) m.dispose();
    this.signMaterials.clear();
    for (const m of this.colorSigns.values()) m.dispose();
    this.colorSigns.clear();
    for (const m of [this.town, this.grass, this.asphalt, this.paver, this.plaza, this.wood, this.tile, this.carpet, this.stone, this.glow, this.water, this.ghost, this.marker]) m.dispose();
    for (const m of this.lazy) m.dispose();
    this.glassMat?.dispose();
    this.unlitMat?.dispose();
    this.plasterTex?.dispose();
    this.brickTex?.dispose();
    for (const t of this.textures) t.dispose();
  }
}
